import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Badge, Button } from "react-bootstrap";
import {
  TableRow,
  CustomCard,
  PageHeading,
  HeaderColumn,
  BootstrapTable,
  TableDetailCell
} from "../common-styling";
import { THUMBNAIL_TYPE } from "../strings";
import TableLoader from "../loader/table-loader";
import NoResultsRow from "../no-results-table-row";
import CountriesAddModal from "./countries-add-modal";
import { flagFromIsoCode } from "../country-flag-emoji";
import { showError } from "../../services/toast-service";
import {
  updateCountry,
  loadCountryOptions,
  selectCountryOptions,
  selectIsCountryOptionsLoading
} from "../../store/slices/countryOptions";

// Site Settings → "Länder": every country option, including hidden ones.
// The list and the form both work on the countryOptions store slice.
const Countries = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { REACT_APP_ASSET_CDN_URL } = process.env;
  const countryOptions = useSelector(selectCountryOptions);
  const isLoading = useSelector(selectIsCountryOptionsLoading);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingOption, setEditingOption] = useState(null);

  useEffect(() => {
    dispatch(loadCountryOptions());
  }, [dispatch]);

  const toggleHidden = (option) =>
    dispatch(
      updateCountry({ id: option._id, body: { hidden: !option.hidden } })
    )
      .unwrap()
      .catch((error) => showError(error.message));

  return (
    <>
      <PageHeading>{t("countries.title")}</PageHeading>
      <div className="text-muted mb-3 ms-1">{t("countries.intro")}</div>
      <div className="d-flex justify-content-end mb-3">
        <Button variant="primary" onClick={() => setShowAddModal(true)}>
          {t("countries.addCountry")}
        </Button>
      </div>
      {isLoading ? (
        <CustomCard className="p-3">
          <table className="w-100">
            <tbody>
              <TableLoader colSpan={1} />
            </tbody>
          </table>
        </CustomCard>
      ) : (
        <CustomCard className="mt-4 mb-3 p-3 rounded-0">
          <BootstrapTable responsive>
            <thead>
              <tr>
                <HeaderColumn>{t("countries.columns.name")}</HeaderColumn>
                <HeaderColumn>{t("countries.columns.countries")}</HeaderColumn>
                <HeaderColumn>{t("countries.columns.dialCode")}</HeaderColumn>
                <HeaderColumn>
                  {t("countries.columns.postalCodeDigits")}
                </HeaderColumn>
                <HeaderColumn>{t("countries.columns.mapImage")}</HeaderColumn>
                <HeaderColumn>{t("countries.columns.status")}</HeaderColumn>
                <HeaderColumn />
              </tr>
            </thead>
            <tbody>
              {countryOptions.length ? (
                countryOptions.map((option) => (
                  <TableRow key={option._id}>
                    <TableDetailCell className="py-3">
                      {option.name}
                    </TableDetailCell>
                    <TableDetailCell className="py-3">
                      {option.countries.map((country) => (
                        <div key={country.isoCode}>
                          {flagFromIsoCode(country.isoCode)} {country.isoCode}
                        </div>
                      ))}
                    </TableDetailCell>
                    <TableDetailCell className="py-3">
                      {option.countries.map((country) => (
                        <div key={country.isoCode}>{country.dialCode}</div>
                      ))}
                    </TableDetailCell>
                    <TableDetailCell className="py-3">
                      {option.countries.map((country) => (
                        <div key={country.isoCode}>
                          {country.postalCodeLengths.join(" / ")}
                        </div>
                      ))}
                    </TableDetailCell>
                    <TableDetailCell className="py-3">
                      {option.mapImageKey ? (
                        <img
                          src={`${REACT_APP_ASSET_CDN_URL}/${option.mapImageKey}/${THUMBNAIL_TYPE.ANSWER_OPTION_ICON}`}
                          alt={option.name}
                          width={60}
                        />
                      ) : (
                        t("countries.bundledMap")
                      )}
                    </TableDetailCell>
                    <TableDetailCell className="py-3">
                      {option.hidden ? (
                        <Badge bg="secondary">
                          {t("countries.statusHidden")}
                        </Badge>
                      ) : (
                        <Badge bg="success">
                          {t("countries.statusActive")}
                        </Badge>
                      )}
                    </TableDetailCell>
                    <TableDetailCell className="py-3">
                      <Button
                        variant="outline-primary"
                        size="sm"
                        className="me-2"
                        onClick={() => {
                          setEditingOption(option);
                          setShowAddModal(true);
                        }}
                      >
                        {t("countries.actions.edit")}
                      </Button>
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => toggleHidden(option)}
                      >
                        {option.hidden
                          ? t("countries.actions.show")
                          : t("countries.actions.hide")}
                      </Button>
                    </TableDetailCell>
                  </TableRow>
                ))
              ) : (
                <NoResultsRow lengthOfColumns="7" />
              )}
            </tbody>
          </BootstrapTable>
        </CustomCard>
      )}
      {showAddModal && (
        <CountriesAddModal
          option={editingOption}
          onClose={() => {
            setShowAddModal(false);
            setEditingOption(null);
          }}
        />
      )}
    </>
  );
};

export default Countries;
